import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@utils/icons';
import type { DailyRouteStop } from '@apis/reports';
import styles from './MovementTimeline.module.css';
import detailStyles from '../AssetDetail.module.css';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';

interface IMovementTimelineProps {
  isLoading: boolean;
  isError: boolean;
  todayStops: DailyRouteStop[];
  assetStatus: string;
}

export function MovementTimeline({ isLoading, isError, todayStops, assetStatus }: IMovementTimelineProps) {
  const { t, i18n } = useTranslation();
  // Arrival/departure times are site-local — an asset's day is the SITE's day.
  const tz = useSelector(selectSiteTz);
  const dash = '—';
  const [open, setOpen] = useState(true);

  return (
    <section className={detailStyles.section}>
      <h4 className={detailStyles.sectionTitle}>
        <button type="button" className={styles.toggle} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <span className={styles.caret} data-collapsed={!open}><ChevronDownIcon size={12} /></span>
          {t('equipment.detail.movementTimeline')}
        </button>
      </h4>
      {open && isLoading && (
        <p className={detailStyles.pendingNote}>{t('equipment.detail.timelineLoading')}</p>
      )}
      {open && isError && (
        <p className={detailStyles.pendingNote}>{t('equipment.detail.timelineUnavailable')}</p>
      )}
      {open && !isLoading && !isError && todayStops.length === 0 && (
        <p className={detailStyles.pendingNote}>{t('equipment.detail.timelineEmpty')}</p>
      )}
      {/* `tz` is undefined until the working site resolves, and
          `timeZone: undefined` silently means the VIEWER's zone — a movement
          stamped in the reader's city rather than the site's. Gate rather than
          default (frontend/CLAUDE.md §6); the loading branch above already
          covers the gap. */}
      {open && todayStops.length > 0 && tz && (
        <ul className={styles.timeline}>
          {todayStops.map((stop, i) => {
            const arrival = new Intl.DateTimeFormat(i18n.language, {
              hour: '2-digit', minute: '2-digit', timeZone: tz,
            }).format(new Date(stop.arrivalIso));
            const departure = new Intl.DateTimeFormat(i18n.language, {
              hour: '2-digit', minute: '2-digit', timeZone: tz,
            }).format(new Date(stop.departureIso));
            const location = [stop.floorNick, stop.areaNick].filter(Boolean).join(' · ') || dash;
            return (
              <li key={i} className={styles.timelineItem}>
                <div className={styles.timelineRail}>
                  <span className={styles.timelineDot} data-status={assetStatus} />
                </div>
                <div className={styles.timelineBody}>
                  <div className={styles.timelineHead}>
                    <span>{arrival}</span>
                    <span className={styles.timelineSep}>–</span>
                    <span>{departure}</span>
                    <span className={styles.timelineSep}>·</span>
                    <span className={styles.timelineLabel}>{stop.durationMinutes}{t('equipment.detail.minutes')}</span>
                  </div>
                  <div className={styles.timelineLocation}>{location}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
